/* The Haaland Code — core theme behavior: analytics, nav, accordions. */
(function () {
  'use strict';

  window.HC = window.HC || {};

  /* ---------- Analytics ---------- */
  HC.track = function track(eventName, payload) {
    payload = payload || {};
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, payload));
      }
    } catch (e) {
      /* ignore */
    }
    document.dispatchEvent(new CustomEvent('hc:event', { detail: Object.assign({ event: eventName }, payload) }));
  };

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- Delegated analytics on [data-hc-event] ---------- */
  function initEventTracking() {
    document.addEventListener('click', function (event) {
      const el = event.target.closest('[data-hc-event]');
      if (!el) return;
      HC.track(el.getAttribute('data-hc-event'), {
        label: el.getAttribute('data-hc-event-label') || el.textContent.trim().slice(0, 80),
      });
    });
  }

  /* ---------- Mobile nav drawer ---------- */
  function initNav() {
    const toggle = document.querySelector('[data-hc-nav-toggle]');
    const drawer = document.querySelector('[data-hc-nav-drawer]');
    if (!toggle || !drawer) return;

    toggle.addEventListener('click', function () {
      const isOpen = drawer.getAttribute('data-open') === 'true';
      drawer.setAttribute('data-open', String(!isOpen));
      toggle.setAttribute('aria-expanded', String(!isOpen));
    });

    drawer.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        drawer.setAttribute('data-open', 'false');
        toggle.setAttribute('aria-expanded', 'false');
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.getAttribute('data-open') === 'true') {
        drawer.setAttribute('data-open', 'false');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });
  }

  /* ---------- Active nav link on scroll ---------- */
  function initActiveNav() {
    const links = document.querySelectorAll('[data-hc-nav-link]');
    if (!links.length || !('IntersectionObserver' in window)) return;
    const sections = [];
    links.forEach(function (link) {
      const id = link.getAttribute('href');
      if (!id || id.charAt(0) !== '#') return;
      const target = document.querySelector(id);
      if (target) sections.push({ link: link, target: target });
    });
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const match = sections.find(function (s) {
            return s.target === entry.target;
          });
          if (!match || !entry.isIntersecting) return;
          links.forEach(function (l) {
            l.classList.remove('hc-active');
          });
          match.link.classList.add('hc-active');
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );

    sections.forEach(function (s) {
      observer.observe(s.target);
    });
  }

  /* ---------- Generic accordion (FAQ, book parts, purchase FAQ) ---------- */
  function initAccordions() {
    document.querySelectorAll('[data-hc-accordion-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);
        if (!panel) return;
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        const group = trigger.closest('[data-hc-accordion-group]');
        const singleOpen = group && group.getAttribute('data-hc-accordion-single') === 'true';

        if (singleOpen && !isOpen) {
          group.querySelectorAll('[data-hc-accordion-trigger]').forEach(function (otherTrigger) {
            if (otherTrigger === trigger) return;
            otherTrigger.setAttribute('aria-expanded', 'false');
            const otherPanel = document.getElementById(otherTrigger.getAttribute('aria-controls'));
            if (otherPanel) otherPanel.style.maxHeight = '0px';
          });
        }

        trigger.setAttribute('aria-expanded', String(!isOpen));
        panel.style.maxHeight = !isOpen ? panel.scrollHeight + 'px' : '0px';
      });
    });
  }

  /* ---------- Card tap-to-expand (seven code elements) ---------- */
  function initExpandCards() {
    document.querySelectorAll('.hc-code-card, .hc-filter-card').forEach(function (card) {
      card.addEventListener('click', function () {
        const wasOpen = card.classList.contains('hc-is-open');
        const parent = card.parentElement;
        if (parent) {
          parent.querySelectorAll('.hc-is-open').forEach(function (other) {
            if (other !== card) other.classList.remove('hc-is-open');
          });
        }
        card.classList.toggle('hc-is-open', !wasOpen);
      });
    });
  }

  /* ---------- Mobile purchase bar: hide near footer / while cart is open ---------- */
  function initMobileBar() {
    const bar = document.querySelector('[data-hc-mobilebar]');
    if (!bar) return;

    document.addEventListener('hc:cart:open', function () {
      bar.setAttribute('data-collapsed', 'true');
    });

    const overlay = document.querySelector('[data-hc-cart-overlay]');
    if (overlay) {
      const observer = new MutationObserver(function () {
        if (overlay.getAttribute('data-open') !== 'true') {
          bar.removeAttribute('data-collapsed');
        }
      });
      observer.observe(overlay, { attributes: true, attributeFilter: ['data-open'] });
    }

    const footer = document.querySelector('.hc-footer');
    if (footer && 'IntersectionObserver' in window) {
      const footerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.setAttribute('data-collapsed', entry.isIntersecting ? 'true' : 'false');
          });
        },
        { rootMargin: '0px' }
      );
      footerObserver.observe(footer);
    }
  }

  /* ---------- Trailer video play overlay ---------- */
  function initVideoPlayOverlay() {
    document.querySelectorAll('[data-hc-video-frame]').forEach(function (frame) {
      const video = frame.querySelector('video');
      const playBtn = frame.querySelector('[data-hc-video-play]');
      if (!video || !playBtn) return;

      playBtn.addEventListener('click', function () {
        video.play();
      });
      video.addEventListener('play', function () {
        frame.setAttribute('data-playing', 'true');
      });
      video.addEventListener('pause', function () {
        frame.setAttribute('data-playing', 'false');
      });
      video.addEventListener('ended', function () {
        frame.setAttribute('data-playing', 'false');
      });
    });
  }

  ready(function () {
    initEventTracking();
    initNav();
    initActiveNav();
    initAccordions();
    initExpandCards();
    initMobileBar();
    initVideoPlayOverlay();
  });
})();
